import { Injectable, Signal, inject, signal } from '@angular/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ValuesService } from '@app/common/values/values.service';
import {
	C_VALUES_LOGISTIC_PARAMS,
	DELIVERY_DEFAULT_DURATION_S,
	DELIVERY_DURATION_STEP_MIN,
	DELRUN_ASK_EXACT_TOLERANCE_MIN,
	DELRUN_PM_SWITCH_HOUR,
	DELRUN_SLACK_S,
	GEOCODE_WARN_SCORE,
} from '@app/shared/constants';

// Paramètres métier réglables du module logistic (tournées, livraisons, géocodage).
export interface LogisticParams {
	default_duration_s: number;		// temps sur place par défaut quand delivery.duration est null (secondes)
	duration_step_min: number;		// pas (minutes) des flèches du champ « Temps approximatif chez le client »
	slack_s: number;							// battement moyen ajouté entre chaque livraison d'une tournée (secondes)
	pm_switch_hour: number;				// board tournées : heure de bascule du filtre par défaut vers l'Après-midi
	geocode_warn_score: number;		// score BAN sous lequel le libellé géocodé est signalé « à vérifier »
	ask_exact_tolerance_min: number;	// « Horaire Fixe » : écart toléré avant de signaler la demande client ratée
}

// Défauts = constantes front : le module fonctionne même sans la ligne c_values en base.
export const LOGISTIC_PARAMS_DEFAULTS: LogisticParams = {
	default_duration_s: DELIVERY_DEFAULT_DURATION_S,
	duration_step_min: DELIVERY_DURATION_STEP_MIN,
	slack_s: DELRUN_SLACK_S,
	pm_switch_hour: DELRUN_PM_SWITCH_HOUR,
	geocode_warn_score: GEOCODE_WARN_SCORE,
	ask_exact_tolerance_min: DELRUN_ASK_EXACT_TOLERANCE_MIN,
};

// Paramétrage serveur : UNE ligne c_values C_VALUES_LOGISTIC_PARAMS (22) dont data porte les clés
// de LogisticParams (JSON). Chargée une fois par session (cache shareReplay de ValuesService),
// re-chargée si ValuesService.updateData() est appelé. Toute clé absente ou non numérique garde
// sa valeur par défaut : la ligne en base peut être partielle.
@Injectable({
	providedIn: 'root'
})
export class LogisticParamsService {

	private valuesService = inject(ValuesService);

	private _params = signal<LogisticParams>({ ...LOGISTIC_PARAMS_DEFAULTS });
	readonly params: Signal<LogisticParams> = this._params.asReadonly();		// snapshot synchrone pour les getters de template

	constructor() {
		this.valuesService.getValues$(C_VALUES_LOGISTIC_PARAMS).pipe(
			catchError(() => of(null)),		// ligne absente / erreur serveur : on reste sur les défauts front
		).subscribe((rows: any) => {
			const data = rows?.[0]?.data;
			if (!data) return;
			const merged: LogisticParams = { ...LOGISTIC_PARAMS_DEFAULTS };
			(Object.keys(LOGISTIC_PARAMS_DEFAULTS) as (keyof LogisticParams)[]).forEach((k) => {
				const v = Number(data[k]);
				if (Number.isFinite(v) && v >= 0) merged[k] = v;		// clé absente / invalide => défaut conservé
			});
			this._params.set(merged);
		});
	}
}
